import React, { useContext } from "react";
import { IntercomSlot, LatestStoriesFilterableList } from "@bloomberg/intercom";
import { BoxAd } from "@bloomberg/media-ui-ads";
import { TerminalTout } from "@bloomberg/media-ui-core";
import { useAbba } from "@foundation/abba-client";
import { useFortress } from "@foundation/fortress";
import { ScreenContext } from "@foundation/screen";
import { useAdOptions } from "@foundation/sparkle/lib/AdOptionsContext";

import BrandMetricsModule, {
    useIsBrandMetricsSurveyRenderer,
} from "~components/BrandMetrics";
import { GridRightContent } from "~components/GridLayout";
import { StickyScroll } from "~components/StickyScroll";
import intercom from "~config/intercom";
import { getAdRefreshOptions } from "~utils/adRefresh";
import { showFirstRightRailAd, AdKey } from "~utils/ads/adLogic";
import { basicArticleContentBlockLength } from "~utils/blockAndCharCountUtil";
import { CurrentStoryContext } from "~utils/currentStoryContext";
import { getFortressState } from "~utils/getFortressState";

import styles from "./rightRail.module.css";
import { TickerAndMostRead } from "./stickySegments";

const RightRail = () => {
    const fortressContext = useFortress();
    const { isAllowed } = getFortressState(fortressContext);
    const { story } = useContext(CurrentStoryContext);
    const {
        breakpoints: { smallDesktop },
    } = useContext(ScreenContext);
    const { adOptions, customRefreshOptions } = useAdOptions({});

    const abbaConfig = useAbba().abbaConfig;
    const shouldRenderSurvey = useIsBrandMetricsSurveyRenderer();

    if (!smallDesktop) {
        return null;
    }

    const numberOfBlocksInStoryBody = basicArticleContentBlockLength(
        story.body.content
    );

    // A single segment maps to 10 Content Blocks of content
    const maxNumberOfSegments = Math.ceil(numberOfBlocksInStoryBody / 10);

    const showBrandMetricsInFirstSegment =
        numberOfBlocksInStoryBody > 5 && maxNumberOfSegments < 3;

    const shouldUseIntercomLatestStories =
        abbaConfig?.["intercom-rightrail"]?.component === "latest";

    const adRefreshOption = getAdRefreshOptions(
        customRefreshOptions,
        adOptions,
        AdKey.RIGHT_RAIL
    );

    return (
        <GridRightContent>
            <StickyScroll.Container
                className={styles.railContainer}
                maxNumberOfSegments={maxNumberOfSegments}
            >
                {showFirstRightRailAd(isAllowed, adOptions.adDemand) && (
                    <StickyScroll.Segment>
                        <BoxAd
                            showPlaceholder={false}
                            positionIncrement={1}
                            flexSizeAllowed
                            adOptions={adRefreshOption}
                        />
                        {showBrandMetricsInFirstSegment && (
                            <BrandMetricsModule
                                className={styles.brandMetric}
                            />
                        )}
                    </StickyScroll.Segment>
                )}

                {isAllowed && (
                    <>
                        <StickyScroll.Segment
                            clingToBottom={maxNumberOfSegments === 1}
                        >
                            <IntercomSlot>
                                <TickerAndMostRead
                                    brand={story.brand}
                                    criteria={() =>
                                        !shouldUseIntercomLatestStories
                                    }
                                />
                                <LatestStoriesFilterableList
                                    intercom={intercom}
                                    criteria={() =>
                                        shouldUseIntercomLatestStories
                                    }
                                    link={{
                                        text: "See all latest",
                                        url: "https://www.bloomberg.com/latest",
                                    }}
                                />
                            </IntercomSlot>
                        </StickyScroll.Segment>

                        {shouldRenderSurvey && (
                            <StickyScroll.Segment>
                                <BrandMetricsModule />
                            </StickyScroll.Segment>
                        )}

                        <StickyScroll.Segment>
                            <BoxAd
                                showPlaceholder={false}
                                positionIncrement={2}
                                flexSizeAllowed
                                adOptions={adRefreshOption}
                            />
                        </StickyScroll.Segment>
                    </>
                )}
            </StickyScroll.Container>
            <TerminalTout className={styles.terminalTout} />
        </GridRightContent>
    );
};

export default RightRail;
